import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/Users/jquense/src/react-formal/www/node_modules/@docpocalypse/gatsby-theme/src/components/PageLayout.tsx";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "migration-to-v2"
    }}>{`Migration to v2`}</h1>
    <p>{`Version 2.0.0 focuses on adding robust hooks support as well as streamlining the public
API towards simplicity and consistency. It also adds first class TypeScript support!`}</p>
    <h2 {...{
      "id": "key-features"
    }}>{`Key Features`}</h2>
    <ul>
      <li parentName="ul">{`New hooks for custom fields and common use-cases makes accessing Form data significantly easier`}<ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`useFormValue`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`useField`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`useFieldArray`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`useFormSubmit`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`useErrors`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`useTouched`}</inlineCode></li>
        </ul></li>
      <li parentName="ul">{`Out-of-the-box support for `}<inlineCode parentName="li">{`<select multiple>`}</inlineCode>{` as well as checkbox groups`}</li>
      <li parentName="ul">{`Better `}<inlineCode parentName="li">{`FieldArray`}</inlineCode>{` validation behavior; arrayHelpers only trigger validation on the list itself, not nested fields`}</li>
      <li parentName="ul">{`Library is now written in TypeScript.`}</li>
    </ul>
    <h2 {...{
      "id": "breaking-changes"
    }}>{`Breaking Changes`}</h2>
    <p>{`There is lot of rewritten code, so there may be subtle changes that I've missed. Please
test in your apps thoroughly.`}</p>
    <h3 {...{
      "id": "package-exports-have-been-rearranged"
    }}>{`Package exports have been rearranged`}</h3>
    <p>{`Previously, most library components and utils were exported as static properties
of the `}<inlineCode parentName="p">{`<Form>`}</inlineCode>{` component as well as being named exports from the package. For
ESM consumers the default export is the `}<inlineCode parentName="p">{`<Form>`}</inlineCode>{` component and
the `}<inlineCode parentName="p">{`<Field>`}</inlineCode>{`, `}<inlineCode parentName="p">{`<FieldArray>`}</inlineCode>{`, `}<inlineCode parentName="p">{`<Message>`}</inlineCode>{`, `}<inlineCode parentName="p">{`<Submit>`}</inlineCode>{`, and `}<inlineCode parentName="p">{`<Summary>`}</inlineCode>{` components
are still static properties of Form:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "static",
        "static": true
      }}>{`import Form from 'react-formal';

<Form>
  <Form.Field />
</Form>;
`}</code></pre>
    <p>{`Every thing else is exported as a named export including an object called `}<inlineCode parentName="p">{`formStatics`}</inlineCode>{`
which is all the static properties on Form.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "static",
        "static": true
      }}>{`import { useField, setter, toFormErrors } from 'react-formal';
`}</code></pre>
    <p>{`For CommonJS consumers the import is the Form component with all other exports as static properties.`}</p>
    <h3 {...{
      "id": "field-render-prop-arguments"
    }}>{`Field render prop arguments`}</h3>
    <p>{`Instead of passing a single `}<inlineCode parentName="p">{`props`}</inlineCode>{` argument and the resolved Input, Field and FieldArrays
now pass props and `}<inlineCode parentName="p">{`meta`}</inlineCode>{` as seperate arguments. The resolved input has been removed
entirely as it was unnecessary for custom components. For details about how fields
resolve to `}<em parentName="p">{`native`}</em>{` elements field `}<inlineCode parentName="p">{`meta`}</inlineCode>{` now contains `}<inlineCode parentName="p">{`nativeTagName`}</inlineCode>{` and `}<inlineCode parentName="p">{`nativeType`}</inlineCode></p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "static",
        "static": true
      }}>{`<Form.Field name="birthDate">
  {(props, meta) => <DatePicker {...props} />}
</Form.Field>
`}</code></pre>
    <p>{`For very custom `}<inlineCode parentName="p">{`Field`}</inlineCode>{` components consider `}<inlineCode parentName="p">{`useField`}</inlineCode>{` and friends.`}</p>
    <h3 {...{
      "id": "change-events---validateon"
    }}>{`Change: `}<inlineCode parentName="h3">{`events`}</inlineCode>{` -> `}<inlineCode parentName="h3">{`validateOn`}</inlineCode></h3>
    <p>{`The validation `}<inlineCode parentName="p">{`events`}</inlineCode>{` prop has been changed to `}<inlineCode parentName="p">{`validateOn`}</inlineCode>{` and limited to only `}<inlineCode parentName="p">{`change`}</inlineCode>{` and `}<inlineCode parentName="p">{`blur`}</inlineCode>{`.
Arbitrary events cannot be configured any longer. To support other sorts of event
handlers use the Field render prop.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<Field>
  {({ onBlur, ...pros }) => <MyInput onValidate={onBlur} {...props} />}
</Field>
`}</code></pre>
    <h3 {...{
      "id": "fields-always-inject-onchange"
    }}>{`Field's `}<em parentName="h3">{`always`}</em>{` inject `}<inlineCode parentName="h3">{`onChange`}</inlineCode></h3>
    <p>{`Previously Fields would only inject handlers for their configured `}<inlineCode parentName="p">{`events`}</inlineCode>{`. This
meant that when set to `}<inlineCode parentName="p">{`null`}</inlineCode>{` a Field could not trigger updates. This has been fixed, so
`}<inlineCode parentName="p">{`events`}</inlineCode>{` (now `}<inlineCode parentName="p">{`validateOn`}</inlineCode>{`) now refer exclusively to handlers `}<em parentName="p">{`that trigger validation`}</em>{` and `}<inlineCode parentName="p">{`onChange`}</inlineCode>{` is always
injected, even when events is `}<inlineCode parentName="p">{`null`}</inlineCode>{`.`}</p>
    <p>{`Note that `}<inlineCode parentName="p">{`onChange`}</inlineCode>{` can still trigger validation, e.g.
`}<inlineCode parentName="p">{`validateOn="change"`}</inlineCode>{` will inject an `}<inlineCode parentName="p">{`onChange`}</inlineCode>{` handler that triggers an update as well as validation,
`}<inlineCode parentName="p">{`validateOn={null}`}</inlineCode>{` will still inject `}<inlineCode parentName="p">{`onChange`}</inlineCode>{` but it will `}<em parentName="p">{`only`}</em>{` trigger an update
to the field value, not validation.`}</p>
    <h3 {...{
      "id": "fieldarray-render-prop-arguments"
    }}>{`FieldArray render prop arguments`}</h3>
    <p><inlineCode parentName="p">{`<FieldArray>`}</inlineCode>{` have switched to be entirely non-presentational components. Meaning,
they no longer accept an `}<inlineCode parentName="p">{`as`}</inlineCode>{` prop or element `}<inlineCode parentName="p">{`children`}</inlineCode>{`. It's generally recommended
that FieldArray be used to compose other `}<inlineCode parentName="p">{`Field`}</inlineCode>{`s via the render prop (but not required).`}</p>
    <p>{`For uses existing renderProp usage the arguments are now:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "static",
        "static": true
      }}>{`<Form.FieldArray>
  {(values, arrayHelpers, meta) => ... }
</Form.FieldArray>
`}</code></pre>
    <p><inlineCode parentName="p">{`useFieldArray`}</inlineCode>{` is also now available as an alternative to render props.`}</p>
    <h4 {...{
      "id": "migrating-fieldarray-components"
    }}>{`Migrating FieldArray Components`}</h4>
    <p>{`This is likely an uncommon pattern, but Field Arrays like the following:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<Form.FieldArray as={MyListComponent} />
`}</code></pre>
    <p>{`Will no longer work, instead switch to the render prop and pass the helpers and values
in as props to your component, or take ad avantage of `}<inlineCode parentName="p">{`useFieldArray`}</inlineCode>{` in MyListComponent.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<Form.FieldArray>
  {(values, arrayHelpers, meta) => (
    <MyListComponent values={values} helpers={arrayHelpers} meta={meta} />
  )}
</Form.FieldArray>
`}</code></pre>
    <h3 {...{
      "id": "fieldarray-validation"
    }}>{`FieldArray validation`}</h3>
    <p>{`Previously, `}<inlineCode parentName="p">{`<FieldArray>`}</inlineCode>{`s would trigger validation the same as `}<inlineCode parentName="p">{`<Field>`}</inlineCode>{`s, validating
the entire branch below their `}<inlineCode parentName="p">{`name`}</inlineCode>{`. Now validation is `}<em parentName="p">{`only`}</em>{` triggered for the list itself.
Practically this means that it's no longer required to set `}<inlineCode parentName="p">{`events={null}`}</inlineCode>{` on FieldArrays as a well
of disabling top level validation.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      